import type { ButtonProps } from '@mui/material/Button';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { CaretDown } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { sizes, spacingTokens, typography } from '../../theme/tokens';
import { Tooltip } from '../Tooltip';
import { FilterBadge } from './FilterBadge';
import { useFilterVariantWarning } from './useFilterVariantWarning';

// Registers the shared `filter` Button variant (styled in theme/components.ts →
// MuiButton.variants). Declared once, here — module augmentation is global, so every trigger in
// the family (including MoreFiltersButton, which still renders its own Button) picks it up.
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    filter: true;
  }
}

/** Named step of the shared max-width scale (`sizes.maxWidth`). */
export type FilterMaxWidth = keyof typeof sizes.maxWidth;

// Label and value share ONE shrinking box, and that is what lets the ORDER of truncation be
// expressed: inside it the value gives up all the width first (the label refuses to shrink at all),
// and only once the value has collapsed does the label start to be clipped by this box's own
// overflow.
//
// Two independent flex siblings CANNOT express that ordering, which was the 2026-08-27 bug.
// Flexbox splits a deficit in proportion to (flex-shrink × base size), so any non-zero shrink on
// the label hands it a share — and half a pixel of deficit is enough to fire its `text-overflow`,
// which then drops whole characters to make room for the "…". "Campaign:" was losing letters while
// the value still had 200px of room to give. Weighting the factors 1-against-100 did not help:
// there is no factor small enough to round a share down to zero.
const StyledTriggerContent = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 1,
  minWidth: 0,
  overflow: 'hidden',
});

// The filter NAME — `flex-shrink: 0`, so it stays whole for as long as the value has anything left
// to give. Past that it is clipped by StyledTriggerContent rather than ellipsised: an ordered pair of
// ellipses isn't expressible in CSS, and a hard clip in that pathological case beats the
// alternative of content spilling outside the button's border.
const StyledTriggerLabel = styled('span')({
  flexShrink: 0,
  whiteSpace: 'nowrap',
});

// The SELECTED VALUE — the only part that shrinks, and the only part that ellipsises. `minWidth: 0`
// is what actually permits a flex item to shrink below its content width; without it
// `text-overflow` never fires (the classic flexbox footgun). Weight (body1Bold vs the root's body1)
// is what makes it read louder than the label: no new colour token, since the active colour already
// comes from the theme's `data-active` rule.
const StyledTriggerValue = styled('span')({
  ...typography.body1Bold,
  marginLeft: spacingTokens.sp4,
  flexShrink: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Every Button is globally `nowrap` (DECISIONS 2026-07-20); restated so the ellipsis doesn't
  // silently depend on that global rule staying put.
  whiteSpace: 'nowrap',
});

export interface FilterTriggerProps extends Omit<ButtonProps, 'variant' | 'endIcon' | 'children' | 'value'> {
  /** The filter's name, e.g. "Period". Always fully visible. */
  label: ReactNode;
  /**
   * The selected value, shown when the filter is down to a **single** value. Takes precedence
   * over `count` — the two are never shown together. Omit / `null` → label only.
   */
  value?: ReactNode;
  /**
   * Full value text for the truncation tooltip. Defaults to `value` — set it when `value` is a
   * ReactNode whose plain text you want in the tooltip.
   */
  valueTooltip?: ReactNode;
  /**
   * Widest the WHOLE trigger may get before the value starts ellipsising — a step of the shared
   * `sizes.maxWidth` scale. Default `xs` (240). A token key rather than a number, so a screen
   * cannot put a raw px value on it. Raise it only for a bar that genuinely has the room.
   */
  maxWidth?: FilterMaxWidth;
  /** Separator printed after the label when a value is shown. Default `":"`. */
  separator?: string;
  /**
   * Number of applied values, shown as a count badge — the summary used once there are **several**
   * values and naming them all would not fit. Ignored while `value` is set.
   */
  count?: number;
  /** Dropdown open — rotates the caret and suppresses the truncation tooltip. */
  open?: boolean;
  /** Force the active (primary) treatment. Defaults to "a value or a count is showing". */
  active?: boolean;
}

/**
 * FilterTrigger — the shared shell for every filter trigger that opens a dropdown: the filter's
 * name, then **either** the selected value **or** a count badge, then a trailing caret — capped at
 * `maxWidth` (the `xs` step, 240, by default), past which the value ellipsises.
 *
 * Value or badge, never both — that is the family's whole display rule, and it lives here so
 * `FilterDropdownButton` and `FilterDateButton` cannot drift apart. One value is worth naming;
 * several are only worth counting. Deciding *which* applies is the wrapper's job (it owns the
 * selection); painting it is this component's.
 *
 * There is **no reserved badge slot** anywhere in the family: a named value is always wider than a
 * badge, so reserving the badge's footprint can no longer keep the caret from moving — it would
 * only add dead width. A badge exists when there is a count, and not otherwise.
 *
 * Low-level on purpose — it owns look + truncation + the tooltip, and nothing else. The overlay,
 * the selection model and the ARIA wiring belong to the wrapper that hosts it
 * (`FilterDropdownButton`, `FilterDateButton`), or to your own composition for a filter shape the
 * family doesn't cover yet.
 *
 * Truncation is a **sighted-user** problem only: CSS `text-overflow` clips the paint, but the full
 * text stays in the accessibility tree, so screen readers already announce the whole value. Hence
 * a Tooltip — shown only when the text really is clipped, measured via `ResizeObserver` — and
 * emphatically not an `aria-label` override, which would *remove* information from SR users. See
 * the `describeChild` note at the render below: MUI's Tooltip does that override by default.
 *
 * Composition — SHARED: `Button`→`ButtonBase`, `Tooltip`→`Popper`. LOCAL: `FilterBadge`.
 */
export const FilterTrigger = forwardRef<HTMLButtonElement, FilterTriggerProps>(function FilterTrigger(
  {
    label,
    value,
    valueTooltip,
    maxWidth = 'xs',
    separator = ':',
    count,
    open = false,
    active,
    sx,
    ...rest
  }: FilterTriggerProps,
  ref
) {
  // Development-only: says so out loud when the theme carrying `variant="filter"` is missing,
  // which otherwise just looks like the design system is broken. See the hook.
  useFilterVariantWarning();

  const hasValue = value != null && value !== '';
  // Value wins: naming the one thing that is on beats counting it.
  const showBadge = !hasValue && count != null && count > 0;

  const contentRef = useRef<HTMLSpanElement>(null);
  const valueRef = useRef<HTMLSpanElement>(null);
  // Which stage of truncation we are in, so the tooltip can offer back exactly what got clipped.
  const [clipped, setClipped] = useState<'none' | 'value' | 'label'>('none');

  // Measured, not assumed, so the tooltip only appears when it adds something. Re-measured on
  // element resize (window resize, filter bar reflow) as well as on content change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the rendered content changes
  useEffect(() => {
    const content = contentRef.current;
    const check = () => {
      // The wrapper can only overflow once the value has collapsed to nothing and the label
      // itself no longer fits — that is stage two, and the label is what is missing.
      if (content && content.scrollWidth > content.clientWidth) {
        setClipped('label');
        return;
      }
      const el = valueRef.current;
      setClipped(el && el.scrollWidth > el.clientWidth ? 'value' : 'none');
    };
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(check);
    if (content) observer.observe(content);
    if (valueRef.current) observer.observe(valueRef.current);
    return () => observer.disconnect();
  }, [value, label, maxWidth]);

  // Empty title = MUI renders no tooltip at all, so the tree stays stable whether or not
  // anything is clipped. Suppressed while the dropdown is open (the overlay is the answer then).
  const tooltipTitle = (() => {
    if (open || clipped === 'none') return '';
    const fullValue = valueTooltip ?? value;
    // Stage two clipped the filter's name too, so give back the whole thing, not just the value.
    if (clipped === 'label') {
      return (
        <>
          {label}
          {hasValue ? `${separator} ` : null}
          {fullValue}
        </>
      );
    }
    return fullValue;
  })();

  // `describeChild` is load-bearing, not a nicety. MUI's Tooltip defaults to applying its title
  // as the child's `aria-label`, which would REPLACE the trigger's accessible name — "Period:
  // May 5, 2025 – May 7, 2026" silently becomes "May 5, 2025 – May 7, 2026" and the filter's
  // name is gone. With `describeChild` the title becomes an `aria-describedby` instead, so the
  // name stays whole and the tooltip is what it should be: supplementary. Only bites once a
  // value is long enough to truncate, which is why it went unnoticed until the max-width cap
  // made truncation common.
  return (
    <Tooltip title={tooltipTitle} describeChild>
      <Button
        ref={ref}
        variant="filter"
        endIcon={<CaretDown />}
        data-active={(active ?? (hasValue || showBadge)) ? 'true' : undefined}
        data-open={open ? 'true' : undefined}
        {...rest}
        // The cap lives on the ROOT, so label + value + caret share one budget and no trigger
        // outgrows the filter bar. Merged as an array rather than set before `...rest`, where a
        // caller passing any `sx` at all would have silently dropped the cap.
        sx={[{ maxWidth: sizes.maxWidth[maxWidth] }, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {/* `data-slot` is the stable hook for targeting any part (tests, and screens that need
              to align a filter bar) without depending on generated class names. */}
        <StyledTriggerContent ref={contentRef} data-slot="content">
          <StyledTriggerLabel data-slot="label">
            {label}
            {hasValue ? separator : null}
          </StyledTriggerLabel>
          {hasValue && (
            <StyledTriggerValue ref={valueRef} data-slot="value">
              {value}
            </StyledTriggerValue>
          )}
        </StyledTriggerContent>
        {/* Outside the shrinking box: a count is short and must never be clipped. */}
        {showBadge && <FilterBadge>{count}</FilterBadge>}
      </Button>
    </Tooltip>
  );
});
